import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase/config';

// ─── helpers ────────────────────────────────────────────────────────────────

const shortAddress = (addr) => {
  if (!addr) return '';
  const parts = addr.split(',').map(p => p.trim());
  let street = '', city = '';
  if (parts.length >= 2) {
    street = parts[0];
    city = parts.length >= 3 ? parts[parts.length - 2] : parts[parts.length - 1];
  } else {
    return parts[0]?.substring(0, 30) || '';
  }
  const s = street && city ? `${street}, ${city}` : city || street;
  return s.length > 30 ? s.substring(0, 27) + '...' : s;
};

// ─── sub-component: one chat row ─────────────────────────────────────────────

const ChatRow = ({ event, lastMessage, userNamesMap, currentUserId }) => {
  const [hovered, setHovered] = useState(false);

  let senderName = '';
  let isUnread = false;

  if (lastMessage) {
    if (lastMessage.user_id === currentUserId) {
      senderName = 'You';
    } else {
      const readBy = lastMessage.read_by || [];
      isUnread = !readBy.includes(currentUserId);
      senderName = userNamesMap[lastMessage.user_id] || 'Someone';
    }
  }

  const isOrganizer = event.creator_id === currentUserId;
  let displayMessage = lastMessage ? `${senderName}: ${lastMessage.message}` : 'No messages yet';
  if (displayMessage.length > 40) displayMessage = displayMessage.substring(0, 37) + '...';
  const lastMessageTime = lastMessage ? (window.formatTimeAgo ? window.formatTimeAgo(lastMessage.created_at) : '') : '';
  const addr = shortAddress(event.place_address);

  return (
    <div
      onClick={() => window.openChatFromList?.(event.event_id, event.title)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '16px 20px',
        borderBottom: '1px solid #e8eaed',
        cursor: 'pointer',
        background: hovered ? '#f1f3f4' : isUnread ? '#f8f9fa' : 'white',
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
        transition: 'background 0.2s'
      }}
    >
      {/* Icon */}
      <div style={{ width: '48px', height: '48px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '2px', position: 'relative' }}>
        {event.place_icon ? (
          <img
            src={event.place_icon}
            alt="Place icon"
            style={{ width: '48px', height: '48px', display: 'block' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div style={{ width: '40px', height: '40px', background: '#757575', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}>
            📍
          </div>
        )}
        {isUnread && (
          <span style={{ position: 'absolute', top: 0, right: 0, width: '10px', height: '10px', background: '#ea4335', borderRadius: '50%', border: '2px solid white' }} />
        )}
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <h4 style={{
            margin: 0, fontSize: '15px', fontWeight: isUnread ? 700 : 600, color: '#202124',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1
          }}>
            {event.title}
          </h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            {isOrganizer && (
              <div style={{ background: '#e3f2fd', color: '#1967d2', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                ORGANIZER
              </div>
            )}
            {lastMessageTime && (
              <span style={{ fontSize: '11px', color: isUnread ? '#202124' : '#9e9e9e', fontWeight: isUnread ? 600 : 400 }}>
                {lastMessageTime}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px' }}>
          <p style={{
            margin: 0, fontSize: '13px',
            fontWeight: isUnread ? 600 : 400,
            color: isUnread ? '#202124' : '#5f6368',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1
          }}>
            {displayMessage}
          </p>
{event.place_name && (
            <span style={{ fontSize: '11px', color: '#9e9e9e', fontStyle: 'italic', maxWidth: '110px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {event.place_name}
            </span>
          )}
        </div>
        {addr && (
          <p style={{ margin: 0, fontSize: '11px', color: '#9e9e9e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {addr}
          </p>
        )}
      </div>
    </div>
  );
};

// ─── main component ──────────────────────────────────────────────────────────

const ChatsListContent = () => {
  const [chats, setChats] = useState(null); // null = loading, [] = empty, [...] = data
  const [userNamesMap, setUserNamesMap] = useState({});
  const [currentUserId, setCurrentUserId] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    // Guard: prevent concurrent loads
    if (window.isLoadingChatsList) return;
    window.isLoadingChatsList = true;

    setChats(null); // show loading
    setError(false);

    try {
      const supabaseClient = window.supabase || supabase;
      const uid = window.currentAppUser?.uid || window.currentUser?.uid;
      if (!uid) { window.isLoadingChatsList = false; return; }
      setCurrentUserId(uid);

      const { data: allEvents, error: eventsErr } = await supabaseClient
        .from('events')
        .select('event_id, title, participants, creator_id, chat_enabled, archived, place_name, place_icon, place_address, start_date_time, end_date_time')
        .or(`creator_id.eq.${uid},participants.cs.{${uid}}`)
        .eq('archived', false)
        .order('created_at', { ascending: false });

      if (eventsErr) throw eventsErr;

      const events = allEvents || [];
      if (events.length === 0) {
        setChats([]);
        window.hasUnreadChats = false;
        window.dispatchEvent(new CustomEvent('unreadChatsChanged'));
        return;
      }

      const eventIds = events.map(e => e.event_id);
      const { data: allMessages } = await supabaseClient
        .from('messages')
        .select('event_id, message, created_at, user_id, read_by')
        .in('event_id', eventIds)
        .order('created_at', { ascending: false });

      const lastMessagesByEvent = {};
      (allMessages || []).forEach(msg => {
        if (!lastMessagesByEvent[msg.event_id]) lastMessagesByEvent[msg.event_id] = msg;
      });

      const uniqueUserIds = [...new Set(
        Object.values(lastMessagesByEvent).map(m => m.user_id).filter(id => id !== uid)
      )];

      const namesMap = {};
      if (uniqueUserIds.length > 0) {
        const { data: users } = await supabaseClient.from('users').select('id, name').in('id', uniqueUserIds);
        (users || []).forEach(u => { namesMap[u.id] = u.name; });
      }
      setUserNamesMap(namesMap);

      const withMessages = events
        .map(event => ({
          event,
          lastMessage: lastMessagesByEvent[event.event_id] || null,
          lastMessageTime: lastMessagesByEvent[event.event_id]
            ? new Date(lastMessagesByEvent[event.event_id].created_at).getTime()
            : 0
        }))
        .sort((a, b) => b.lastMessageTime - a.lastMessageTime);

      setChats(withMessages);

      const hasAnyUnread = withMessages.some(({ lastMessage }) => {
        if (!lastMessage || lastMessage.user_id === uid) return false;
        return !(lastMessage.read_by || []).includes(uid);
      });
      window.hasUnreadChats = hasAnyUnread;
      window.dispatchEvent(new CustomEvent('unreadChatsChanged'));

    } catch (err) {
      console.error('Error loading chats:', err);
      setError(true);
    } finally {
      window.isLoadingChatsList = false;
    }
  }, []);

  useEffect(() => {
    // Expose load function so chatsList.js can still call window.loadChatsList
    window.loadChatsList = load;
    window.loadChatsListDebounced = (() => {
      let t;
      return () => { clearTimeout(t); t = setTimeout(load, 300); };
    })();

    const handleLoad = () => load();
    window.addEventListener('loadChatsList', handleLoad);
    return () => window.removeEventListener('loadChatsList', handleLoad);
  }, [load]);

  // ── render ──

  if (chats === null && !error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
        <div className="loading-spinner" style={{ width: '40px', height: '40px', border: '3px solid #f0f0f0', borderTop: '3px solid #00a3b7', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', color: '#ea4335', fontSize: '14px' }}>
        Failed to load chats
      </div>
    );
  }

  if (chats && chats.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', textAlign: 'center' }}>
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#dadce0" strokeWidth="2" style={{ marginBottom: '16px' }}>
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        <p style={{ margin: 0, color: '#5f6368', fontSize: '16px', fontWeight: 500 }}>No chats yet</p>
        <p style={{ margin: '8px 0 0', color: '#9e9e9e', fontSize: '14px' }}>Join or create an event to start chatting</p>
      </div>
    );
  }

  return (
    <div>
      {(chats || []).map(({ event, lastMessage }) => (
        <ChatRow
          key={event.event_id}
          event={event}
          lastMessage={lastMessage}
          userNamesMap={userNamesMap}
          currentUserId={currentUserId}
        />
      ))}
    </div>
  );
};

export default ChatsListContent;